import { useQuery } from '@tanstack/react-query'
import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import {
  selectFilteredVirtualCards,
  selectVirtualCardsFullPaginatedData,
} from 'src/app/VirtualCardsNeedsApproval/redux/virtualCardsNeedsApproval.selectors'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import PendingRequestsBox from 'src/components/PendingRequestsBox/PendingRequestsBox'
import { VirtualCardsFiltersBody } from 'src/components/VirtualCardsBoxAndLinks/VirtualCardsBoxAndLinks'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useBackgroundDispatch } from 'src/hooks'
import { orgCardApprovalRulesQueries } from 'src/queries/organizations/cardApprovalRules/cardApprovalRules.queries'
import { getVirtualCards } from 'src/redux/actions/data/virtualCards.actions'
import {
  selectAllStatisticsLoadedFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import {
  selectVirtualCardsCardholderPendingRequestOrApproverRuleApprovalCountStatisticsFromState,
  selectVirtualCardsPendingRequestCardholderMeCountStatisticsFromState,
} from 'src/redux/selectors/data/statistics.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'

const VirtualCardsNeedsApproval: React.FC = () => {
  const virtualCardsFullPaginatedData = useSelector(
    selectVirtualCardsFullPaginatedData,
  )
  const filteredVirtualCards = useSelector(selectFilteredVirtualCards)
  const appName = useSelector(selectAppName)
  const dispatch = useBackgroundDispatch()
  const user = useCurrentUser()
  const cardApprovalRulesQuery = useQuery(
    orgCardApprovalRulesQueries.detail(user?.organizationId),
  )
  const cardApprovalRules = cardApprovalRulesQuery.data
  const cardApprovalRulesFetched = cardApprovalRulesQuery.isFetched
  const isUserOrganizationCardApprover =
    cardApprovalRules?.approverId === user?.id
  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const virtualCardsPendingRequestCardholderMeCount = useSelector(
    selectVirtualCardsPendingRequestCardholderMeCountStatisticsFromState,
  )
  const virtualCardsCardholderPendingRequestOrApproverRuleApprovalCountStatistics =
    useSelector(
      selectVirtualCardsCardholderPendingRequestOrApproverRuleApprovalCountStatisticsFromState,
    )
  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)

  useEffect((): void => {
    dispatchStatistics([
      appStatisticsKeys.VirtualCardsCardholderPendingRequestOrApproverRuleApprovalCountStatistics,
      appStatisticsKeys.VirtualCardsPendingRequestCardholderMeCountStatistics,
    ])
  }, [dispatchStatistics])

  const fetchVirtualCards = (
    filtersBody: VirtualCardsFiltersBody,
    page?: number,
  ): void => {
    if (cardApprovalRulesFetched) {
      dispatch(
        getVirtualCards({
          count: 10,
          page: page,
          cardholder: 'me',
          pendingRequest: true,
          ...filtersBody,
        }),
      )
    }
  }
  const fetchVirtualCardsForCardApprover = (
    filtersBody: VirtualCardsFiltersBody,
    page?: number,
  ): void => {
    dispatch(
      getVirtualCards({
        count: 10,
        page: page,
        cardholderPendingRequestOrApproverRuleApproval: 'me',
        ...filtersBody,
      }),
    )
  }

  const infiniteScrollProps = {
    ...virtualCardsFullPaginatedData,
    fetch: fetchVirtualCards,
  }

  const cardApproverScrollProps = {
    ...virtualCardsFullPaginatedData,
    fetch: fetchVirtualCardsForCardApprover,
  }

  return (
    <>
      <Helmet>
        <title>{`Requests | ${appName}`}</title>
        <meta name="description" content={`Requests page of ${appName}`} />
      </Helmet>
      <BackgroundPageContent noPadding>
        <PendingRequestsBox
          virtualCards={filteredVirtualCards}
          infiniteScrollProps={infiniteScrollProps}
          isUserCardApprover={isUserOrganizationCardApprover}
          cardApprovalRulesFetched={cardApprovalRulesFetched}
          cardApproverScrollProps={cardApproverScrollProps}
          showLoadingPage={
            !statisticsLoaded.virtualCardsCardHolderPendingRequestOrApproverRuleApprovalLoaded ||
            !statisticsLoaded.virtualCardsPendingRequestCardholderMeCountStatisticsLoaded
          }
          totalCount={
            isUserOrganizationCardApprover
              ? virtualCardsCardholderPendingRequestOrApproverRuleApprovalCountStatistics
              : virtualCardsPendingRequestCardholderMeCount
          }
        />
      </BackgroundPageContent>
    </>
  )
}

export default VirtualCardsNeedsApproval
